import cx from 'clsx';
import React from 'react';

import AssetLayer from '@/components/AssetLayer/AssetLayer';
import {
  JackpotLayerGameTile,
  LiveTableLayer,
  QuicksilverTileOutfitType,
  SashLayer,
} from '@/components/game-tile';
import AnimatedAssetLayer from '@/components/game-tile/AnimatedAssetLayer/AnimatedAssetLayer';
import GameTile from '@/components/game-tile/GameTile';
import LayersWrapper from '@/components/game-tile/LayersWrapper';
import GameLink from '@/components/GameLink';
import { Info } from '@/components/icons';
import IntersectionObserverWrapper from '@/components/IntersectionObserverWrapper';
import { ShouldPreventDefaultForClick } from '@/components/LaunchStrategiesProvider/utils';
import { SMALL_OUTFIT } from '@/components/modular-grid/constants';
import GhostTile from '@/components/modular-grid/GhostTile';
import { GameClickProps, GameTileProps } from '@/components/modular-grid/types';
import { prepareGameInfoTooltip } from '@/components/modular-grid/utils';
import { UserSessionContext } from '@/components/UserSessionProvider';
import { IMAGE_SIZES } from '@/model/algolia/constants';
import { transformImage } from '@/model/algolia/utils';
import {
  CategoryFinancialWebAttributes,
  CategoryStaticTexts,
} from '@/types/category/categoryTypes';
import { AnimationEffect, ImageType } from '@/types/gameTypes';

import styles from './ModularGridGameTile.module.scss';

export type TileSize =
  | 'small'
  | 'large'
  | 'vertical'
  | 'tall-vertical'
  | 'wide-horizontal';

interface ModularGridGameTileProps {
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  game?: GameTileProps;
  onClick?: (game: GameClickProps) => void;
  onInfoClick?: (game: GameClickProps, path: string) => void;
  outfit: QuicksilverTileOutfitType;
  isLazy?: boolean;
  isAnchor?: boolean;
  ghost?: boolean;
  tileSize?: TileSize;
  categoryAttributes?: CategoryFinancialWebAttributes;
  renderExtendedInfo?: (
    game: GameTileProps,
    staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>
  ) => JSX.Element | null;
  staticTexts?: Pick<CategoryStaticTexts, 'bingoTimer'>;
  shouldShowSash?: boolean;
  shouldShowJackpot?: boolean;
  isIntersectionObserverDisabled?: boolean;
  className?: string;
}

export const prepareImages = (
  gameImage: ImageType | undefined,
  tileSize: TileSize
) => {
  return IMAGE_SIZES[tileSize].map(config => ({
    ...config,
    src: transformImage(gameImage?.source.base, config.size),
  }));
};

export const getSizedSources = (
  sizedImages: GameTileProps['sizedImages'],
  image: ImageType | undefined,
  tileSize: TileSize
) => {
  return sizedImages
    ? sizedImages[tileSize === 'small' ? 'smallTile' : 'largeTile']
    : prepareImages(image, tileSize);
};

const handleGameTileClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  onClick: ModularGridGameTileProps['onClick'],
  game: GameTileProps,
  preventDefaultForGameClick: boolean
) => {
  if (preventDefaultForGameClick) e.preventDefault();
  onClick && onClick(prepareGameClickProps(game));
};

const handleGameTileInfoIconClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  onInfoClick: ModularGridGameTileProps['onInfoClick'],
  game: GameTileProps,
  gameDetailsPath: string
) => {
  e.preventDefault();
  e.stopPropagation();
  onInfoClick && onInfoClick(prepareGameClickProps(game), gameDetailsPath);
};

export const prepareGameClickProps = (game: GameTileProps) => ({
  id: game.id,
  title: game.title,
  name: game.name,
  provider: game.provider,
  integration: game.integration,
  presentation_code: game.presentation_code,
  transaction_code: game.transaction_code,
  transactional_game_code: game.transactional_game_code,
  launchUrl: game.launchUrl,
  gameDetailsPath: game.gameDetailsPath,
  gameLaunchPath: game.gameLaunchPath,
  type: game.type,
  trackingTitle: game.tracking_metadata?.title || game.title,
  sash: game.sash,
});

const getSizedImages = (
  game: GameTileProps,
  loggedIn: boolean,
  tileSize: TileSize
) => {
  switch (tileSize) {
    // New tiles will not use 'sizesImage'
    case 'tall-vertical':
    case 'wide-horizontal':
    case 'vertical': {
      return undefined;
    }
    default: {
      return loggedIn && game?.sizedImagesLoggedIn
        ? game?.sizedImagesLoggedIn
        : game?.sizedImages;
    }
  }
};

/**
 * Retrieves the appropriate image for a game based on the tile size and user
 * login status.
 */
const getGameImage = (
  game: GameTileProps,
  loggedIn: boolean,
  tileSize: TileSize
): ImageType | undefined => {
  if (!game) return;

  switch (tileSize) {
    case 'vertical': {
      return loggedIn && game.vertical_image_logged_in
        ? game.vertical_image_logged_in
        : game.vertical_image;
    }

    case 'tall-vertical': {
      return loggedIn && game.tall_vertical_image_logged_in
        ? game.tall_vertical_image_logged_in
        : game.tall_vertical_image;
    }
    case 'wide-horizontal': {
      return loggedIn && game.wide_landscape_image_logged_in
        ? game.wide_landscape_image_logged_in
        : game.wide_landscape_image;
    }
    case 'small': {
      return loggedIn && game.image_logged_in
        ? game.image_logged_in
        : game.image;
    }
    // the same assets as 'small', but different sizes for srcset
    default: {
      return loggedIn && game.image_logged_in
        ? game.image_logged_in
        : game.image;
    }
  }
};

const isSquare = (tileSize: TileSize) => {
  return tileSize === 'small' || tileSize === 'large';
};

const ModularGridGameTile: React.FC<ModularGridGameTileProps> = ({
  game,
  onClick,
  onInfoClick,
  outfit,
  isLazy = false,
  isAnchor = false,
  ghost,
  gameDetailsInfo,
  gameInfoTooltip,
  tileSize = 'small',
  renderExtendedInfo,
  categoryAttributes,
  staticTexts,
  shouldShowSash = true,
  shouldShowJackpot = true,
  isIntersectionObserverDisabled = false,
  className = '',
}) => {
  const { loggedIn } = React.useContext(UserSessionContext);

  if (ghost) {
    return (
      <div className={styles.ghostTileContainer}>
        <GhostTile />
      </div>
    );
  }
  if (!game) return null;

  const preventDefaultForGameClick = ShouldPreventDefaultForClick(
    game.integration
  );

  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    gameInfoTooltip,
    game.title
  );

  const validateLayerAnimation = (animation: GameTileProps['animation']) => {
    if (!animation || !animation.animationType) return false;
    if (
      animation.animationType === AnimationEffect.sparkleLoop &&
      !animation.asset
    )
      return false;
    return true;
  };

  const sizedImages = getSizedImages(game, loggedIn, tileSize);
  const gameImage = getGameImage(game, loggedIn, tileSize);
  const stretchable = !isSquare(tileSize);

  return (
    <IntersectionObserverWrapper
      className={styles.container}
      loadingClassName={styles.loadingWrapper}
      disabled={isIntersectionObserverDisabled}
      dataTestId="game-tile-wrapper"
    >
      <GameTile
        data-testid="game-tile"
        stretchable={stretchable}
        attachAnimationStyles={game?.animation}
      >
        <LayersWrapper
          className={className}
          rounded={renderExtendedInfo ? 'top' : 'all'}
        >
          {validateLayerAnimation(game.animation) ? (
            <AnimatedAssetLayer
              game={game}
              data-testid="animated-game-tile"
              isLazy={isLazy}
              tileSize={tileSize}
            />
          ) : (
            <AssetLayer
              type="image"
              sizedSources={getSizedSources(sizedImages, gameImage, tileSize)}
              loadingAnimation={game.loading}
              data-testid="picture-layer"
              imageSrc={gameImage?.source}
              alt={gameImage?.alt || ''}
              rounded={renderExtendedInfo ? 'top' : 'all'}
            />
          )}
        </LayersWrapper>
        {isAnchor && game.launchUrl && (
          <GameLink
            url={game.launchUrl}
            categoryAttributes={categoryAttributes}
            target="_blank"
            onClick={e =>
              handleGameTileClick(e, onClick, game, preventDefaultForGameClick)
            }
            notIndexed={game.notIndexed}
            integration={game.integration}
          ></GameLink>
        )}
        {gameDetailsInfo && (
          <a
            className={cx(styles.info, styles.link)}
            target="_blank"
            rel="noopener noreferrer"
            href={game.gameDetailsPath}
            title={preparedGameInfoTooltip}
            onClick={e =>
              handleGameTileInfoIconClick(
                e,
                onInfoClick,
                game,
                game.gameDetailsPath
              )
            }
          >
            <div className={styles.infoWrapper}>
              <Info altText={preparedGameInfoTooltip} />
            </div>
          </a>
        )}
        {game.table && <LiveTableLayer table={game.table} outfit={outfit} />}
        {game.jackpot &&
          !game.table &&
          outfit !== SMALL_OUTFIT &&
          shouldShowJackpot && <JackpotLayerGameTile {...game.jackpot} />}
        {shouldShowSash && game.sash && (
          <SashLayer
            sash={game.sash.title}
            outfit={outfit}
            textColor={game.sash.text_color}
            textBackgroundColor={game.sash.text_background_color}
            background={game.sash.background}
          />
        )}
      </GameTile>
      {renderExtendedInfo && renderExtendedInfo(game, staticTexts)}
    </IntersectionObserverWrapper>
  );
};

export default ModularGridGameTile;
